import { useCallback, useContext, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentButton from '../../../draft-content-transplant/ContentButton';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import UITextToolbarIcon from 'rich-text-lib/components/editor/UITextToolbarIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import InsertContentDecorator from '../../../draft-transplant/InsertContentDecoratorV2';
import hubHttpClient from 'hub-http/clients/apiClient';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import PortalIdParser from 'PortalIdParser';
import identity from 'transmute/identity';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import { meetingsApiWrapper } from './MeetingsApi';
import GenericPopoverHeader from '../../components/GenericPopoverHeader';
import MeetingPopoverEmptyState from './MeetingPopoverEmptyState';
import GenericPopoverErrorState from '../../components/GenericPopoverErrorState';
import { insertLink } from '../../utils/documentUtils';
import ContentContainer from '../../components/insertMenu/ContentContainer';
import { getMeetingsUrl } from '../../utils/manageLinks';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import { getGlobalVars } from '../../utils/configUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const MeetingsButton = ({
  onClick
}) => /*#__PURE__*/_jsx(UITooltip, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.tooltips.insertMeeting"
  }),
  children: /*#__PURE__*/_jsx(RTEToolbarButton, {
    "data-test-id": "meetings",
    onClick: onClick,
    selected: false,
    children: /*#__PURE__*/_jsx(UITextToolbarIcon, {
      name: "meetings"
    })
  })
});
export default function InsertMeetings({
  onInsertContent = identity,
  onOpenPopover = identity,
  apiWrapper = meetingsApiWrapper,
  apiClient = hubHttpClient,
  outputType = contentOutputs.RENDERED,
  Button = MeetingsButton,
  Header = GenericPopoverHeader,
  PopoverComponent = UIPopover,
  useContentContainer,
  onViewInsertMenu = () => {},
  onInsertComplete = () => {}
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const {
    usePlaintextMode,
    plaintextOptions
  } = getGlobalVars(useContext(EditorConfigContext));
  const insertMeeting = useCallback(({
    htmlBody
  }) => {
    const tr = insertLink({
      htmlBody,
      editorState,
      to: editorState.selection.to,
      from: editorState.selection.from
    });
    setEditorState(usePlaintextMode ? convertToPlaintextState(editorState.apply(tr), plaintextOptions) : editorState.apply(tr));
    onInsertComplete();
  }, [editorState, setEditorState, usePlaintextMode, plaintextOptions, onInsertComplete]);
  const MeetingsPopover = useMemo(() => {
    const apiFunctions = apiWrapper(apiClient);
    const insertMeetingsDecoratorConfig = {
      outputType,
      apiFunctions
    };
    if (useContentContainer) {
      return InsertContentDecorator(ContentContainer({
        onInsertContent,
        // @ts-expect-error portalId can be undefined in unit testing for example
        portalId: PortalIdParser.get(),
        PopoverErrorState: GenericPopoverErrorState,
        PopoverEmptyState: MeetingPopoverEmptyState,
        Header,
        searchPlaceHolder: 'draftPlugins.meetings.popover.search.searchPlaceholder',
        search: apiFunctions.searchArticles,
        onViewInsertMenu,
        // @ts-expect-error portalId can be undefined in unit testing for example
        manageUrl: getMeetingsUrl(PortalIdParser.get()),
        titleMessage: 'draftPlugins.meetings.popover.header.insertMeeting'
      }), insertMeetingsDecoratorConfig);
    }
    return InsertContentDecorator(ContentButton({
      onInsertContent,
      // @ts-expect-error ts-migrate FIXME
      portalId: PortalIdParser.get(),
      Button,
      PopoverErrorState: GenericPopoverErrorState,
      PopoverEmptyState: MeetingPopoverEmptyState,
      PopoverComponent,
      Header,
      onOpenPopover,
      popoverClassName: 'prosemirror-meetings-popover',
      searchPlaceHolder: 'draftPlugins.meetings.popover.search.searchPlaceholder',
      search: apiFunctions.searchArticles,
      popoverTestKey: 'meetings-popover',
      // @ts-expect-error portalId can be undefined in unit testing for example
      manageUrl: getMeetingsUrl(PortalIdParser.get()),
      titleMessage: 'draftPlugins.meetings.popover.header.insertMeeting'
    }), insertMeetingsDecoratorConfig);
  }, [onInsertContent, onOpenPopover, apiClient, apiWrapper, outputType, Button, PopoverComponent, Header, onViewInsertMenu, useContentContainer]);
  return /*#__PURE__*/_jsx(MeetingsPopover, {
    onChange: insertMeeting,
    useProsemirror: true
  });
}